import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTheme as useWidgetTheme } from "styled-components";
import { useAppDispatch, useAppSelector } from "../../Store";
import { allMessagesStore, getLanguageSelector } from "../../Store/Slices/socket/AllMessagesSlice";
import { botInfoStore, setUserExpanded, setIsPipHistoryLoading } from "../../Store/Slices/socket/BotInfoSlice";
import { deviceSizeStore } from "../../Store/Slices/socket/DeviceSizeSlice";
import { DEVICE_SIZE } from "../../Customization/BreakPoints";
import {
  CUSTOM_HEIGHT_BOUNDS,
  CUSTOM_WIDTH_BOUNDS,
  clampCustomSize,
  resolveDockedFrameWidth,
  Surface,
  WIDGET_SIZE_PRESETS,
} from "../../Models/ChatbotConfiguration";
import { StyledChatUIBoxWrap } from "../Styles/StyledScreens";
import AppChatScreenWrapper from "../appComponents/UtilsComponents/AppChatScreenWrapper";
import useMessagesData from "../hooks/useMessagesData";
import { PreviewType } from "../utils/Enum";
import AppInputHandler from "./AppInputHandler";
import Header from "./Header";
import WidgetCopyright from "./WidgetCopyright";
import AppChatLoadingScreen from "../appComponents/UtilsComponents/AppChatLoadingScreen";
import { useBotConfigStore } from "../../Store/Slices/ChatbotSlices/BotConfigSlice";
import useCrossWindowMessageSync from "../hooks/useCrossWindowMessageSync";

interface Props {
  isFullScreen?: boolean;
  isMobileView?: boolean;
  surface?: Surface;
}

const MainUI: React.FC<Props> = ({ isFullScreen,isMobileView,surface }) => {
  const dispatch = useAppDispatch();
  const { chatOpened, widgetPreviewType, isPipInstance } =
    useAppSelector(botInfoStore);

  useCrossWindowMessageSync();

  const wasChatOpenedRef = useRef(chatOpened);
  useLayoutEffect(() => {
    if (chatOpened && !wasChatOpenedRef.current) {
      dispatch(setUserExpanded(false));
    }
    wasChatOpenedRef.current = chatOpened;
  }, [chatOpened, dispatch]);

  // getSlideUpAnimationValue rebuilds its keyframes (and therefore restarts
  // the 0.2s open animation from height:0) every time deviceheight changes —
  // not just on the open/close transition it's meant for. deviceheight moves
  // on every browser-zoom step (via the devicePixelRatio effect in Home.tsx),
  // so zooming while the panel is already open kept replaying the whole
  // open animation from scratch, and a screenshot taken mid-replay looks like
  // a wrongly-sized/cropped panel even though the target height is correct.
  // Once the entrance animation has had time to finish, hasSettled lets
  // StyledChatUIBoxWrap switch to a plain height that tracks deviceheight
  // immediately, instead of re-animating on every subsequent resize.
  const [hasSettled, setHasSettled] = useState(false);
  useEffect(() => {
    if (!chatOpened) {
      setHasSettled(false);
      return;
    }
    const t = setTimeout(() => setHasSettled(true), 220);
    return () => clearTimeout(t);
  }, [chatOpened]);
  const deviceSize = useAppSelector(deviceSizeStore);
  const { height, width, actualWidth } = deviceSize;
  // actualWidth is the raw device width the host reported, before any preset
  // capping — 0 until the first resize handshake, so an unconfirmed reading
  // isn't treated as mobile.
  const isMobileViewport = !!actualWidth && actualWidth < DEVICE_SIZE.tablet;
  const { sessionId, allMessages, botLanguage } =
    useAppSelector(allMessagesStore);
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [disableStartScreen, setDisableStartScreen] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const languageSelector = useAppSelector(getLanguageSelector);
  const hasFetchedRef = useRef(false);
  // const [disableHeaderSelection, setDisableHeaderSelection] = useState<boolean>(false);
  //custom hooks
  // console.log("MainUI Rendered",allMessages);
  const {
    getNewMessages,
    getHistoryMessages,
    loading,
    getNewMessagesForDirectLivechat,
    requestParams,
  } = useMessagesData();

  // Lets Home.tsx show a loading overlay over the whole panel while the
  // PiP copy is still waiting for its conversation state right after "Keep
  // chatting here" — covers the moment before EITHER of its two sources
  // (this instance's own REST fetch below, or useCrossWindowMessageSync's
  // relay of the primary instance's live state) has delivered anything yet,
  // so the panel never shows briefly empty content before real history
  // renders.
  useEffect(() => {
    if (!isPipInstance || !chatOpened) return;
    if (allMessages?.length > 0) {
      dispatch(setIsPipHistoryLoading(false));
      return;
    }
    dispatch(setIsPipHistoryLoading(true));
    // Safety net: a genuinely fresh conversation (no history yet either
    // side) would otherwise leave this overlay showing forever, since
    // allMessages?.length===0 would never flip. This feature only fires
    // with a conversation already in progress, so this shouldn't trigger
    // in practice, but caps the overlay at 4s regardless.
    const timeout = setTimeout(() => dispatch(setIsPipHistoryLoading(false)), 4000);
    return () => clearTimeout(timeout);
  }, [isPipInstance, chatOpened, allMessages, dispatch]);

  const { botConfig } = useAppSelector(useBotConfigStore);
  const conversationLimit = useMemo(
    () => botConfig?.config?.conversation,
    [botConfig]
  );
  const directLiveChatEnabled = useMemo(
    () =>
      botConfig?.chatbot?.hasOwnProperty("bot_enabled")
        ? !botConfig.chatbot.bot_enabled
        : false,
    [botConfig]
  );

  // ********************************************************

  // ─── REQUEST DATA EVENTS ──────────────────────────────────────────────────────────────

  // ********************************************************
  const isOldVisitor = useMemo(
    () => sessionId?.length > 0 && widgetPreviewType !== PreviewType.getaWidgetPreview,
    [sessionId, widgetPreviewType]
  );
  
  // Reset fetch flag when sessionId changes to a valid value (not null/empty)
  // Don't reset when clearing session (null) to prevent duplicate fetches
  useEffect(() => {
    if (sessionId?.length > 0) {
      // console.log("[MainUI] Resetting hasFetchedRef due to new sessionId:", sessionId);
      hasFetchedRef.current = false;
    }
  }, [sessionId]);
  
  const getMessages = () => {
    // console.log("[MainUI getMessages] Called. isOldVisitor:", isOldVisitor, "sessionId:", sessionId, "requestParams:", requestParams);
    // const isOldVisitor =
    //   sessionId?.length > 0 &&
    //   widgetPreviewType !== PreviewType.getaWidgetPreview;

    if (isOldVisitor) {
      getHistoryMessages(requestParams);
    } else if (directLiveChatEnabled) {
      getNewMessagesForDirectLivechat({
        ...requestParams,
        session_id: null,
        bot_enabled: false,
        language: botLanguage,
      });
    } else {
      //get new messgaes
      getNewMessages({ ...requestParams, language:botLanguage });
    }
  };

  const onFormSubmitted = useCallback(() => {
    setFormSubmitted(true);
  }, []);

  // useEffect(() => {
  //   setFormSubmitted(false);
  // }, []);

  useEffect(() => {
    // The PiP copy relies entirely on useCrossWindowMessageSync's
    // request+relay of the primary instance's own live allMessages — it
    // must NOT also run its own REST history fetch here. The two sources
    // assign different message_id values for the very same message (this
    // fetch returns the backend's own server-assigned id, while the
    // primary's relayed copy still carries the client-generated uniqid()
    // id from whichever side originally sent it), which broke the
    // cross-window sync guard's message_id matching — the same logical
    // message looked like two different ones depending on which source
    // populated it first, permanently blocking later relays. The PiP
    // copy's initial state comes from the relay's own initial-sync
    // request instead (see useCrossWindowMessageSync.tsx).
    if (isPipInstance) return;
    if (!chatOpened) return;
    if (!conversationLimit?.enabled) return;
    if (allMessages?.length > 0) return;
    if (hasFetchedRef.current) return; // Prevent duplicate fetches

    if (isOldVisitor) {
      // console.log("[MainUI useEffect] Fetching for old visitor with session:", sessionId);
      hasFetchedRef.current = true;
      getMessages();
      setShowStartScreen(false);
      setFormSubmitted(true);
      return;
    }
    if (!languageSelector) {
      // console.log("[MainUI useEffect] Fetching for new visitor");
      hasFetchedRef.current = true;
      getMessages();
    }
  }, [
    chatOpened,
    // formSubmitted,
    // botConfig?.config?.start_screen,
    botLanguage,
    languageSelector,
    allMessages,
    conversationLimit,
    isOldVisitor,
    isPipInstance,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // The PiP copy's showStartScreen/formSubmitted never get set by the
    // effect above (it returns immediately for isPipInstance — see its own
    // comment), since that only happens as a side effect of the REST fetch
    // this copy intentionally skips. Its conversation state instead
    // arrives via useCrossWindowMessageSync's relay — once that's produced
    // any messages (meaning the user had already picked a language/started
    // chatting in the primary, or in this PiP copy itself), the start
    // screen (which is what actually renders the language selector) must
    // not show again here either.
    if (!isPipInstance) return;
    if (allMessages?.length === 0) return;
    setShowStartScreen(false);
    setFormSubmitted(true);
  }, [isPipInstance, allMessages]);

  // Same resolved flag the styling uses (see WidgetApp). Bots not on the
  // redesign keep the previous hard-coded 376x610 frame.
  const widgetTheme: any = useWidgetTheme();
  const isNewUi = !!widgetTheme?.industry_style?.enabled;

  const widgetSizePreset = useMemo(() => {
    if (!isNewUi) return null;
    const size = (botConfig?.config?.widget_size ?? "standard") as keyof typeof WIDGET_SIZE_PRESETS;
    return WIDGET_SIZE_PRESETS[size] ?? WIDGET_SIZE_PRESETS.standard;
  }, [botConfig, isNewUi]);

  // Master applied no bounds to these; only the redesign clamps them.
  const resolveCustomSize = (value: unknown, bounds: { min: number; max: number }) =>
    isNewUi ? clampCustomSize(value, bounds) : (Number(value) || null);

  const frameWidth = useMemo(() => {
    // Expanded is sized by SURFACE_PANEL_WIDTH/HEIGHT in StyledChatUIBoxWrap,
    // which ignores devicewidth — so this value is unused on that surface. The
    // store holds the docked size (see Home), not the expanded one.
    if (surface === "expanded") return width;
    return resolveDockedFrameWidth({
      preset: widgetSizePreset,
      customWidth: resolveCustomSize(
        botConfig?.config?.custom_width,
        CUSTOM_WIDTH_BOUNDS
      ),
      storeWidth: width,
      isMobileViewport,
      actualWidth,
    });
   }, [width,botConfig,surface,widgetSizePreset,isMobileViewport,actualWidth]);

  const frameHeight = useMemo(() => {
    const autoHeight = botConfig?.config?.auto_height ?? false;
    const modifiedFinalValue = height - (botConfig?.config?.auto_height_limiting_number ?? 0);
    // If window height is less than 620px, make frameHeight equal to window height.
    // Presets/custom height are also skipped on a confirmed mobile viewport —
    // height<=620 alone misses a tall phone in portrait (e.g. 844px), which
    // would otherwise still get the Tall preset's 858px and overflow.
    if (height <= 620 || isMobileViewport) {
      return height;
    }

    if (!isNewUi) {
      // Master had no custom-height branch and a fixed 610 ceiling.
      const legacyMax = autoHeight ? modifiedFinalValue : 610;
      return legacyMax ?? 610;
    }

    const customHeight = resolveCustomSize(
      botConfig?.config?.custom_height,
      CUSTOM_HEIGHT_BOUNDS
    );
    if (customHeight) {
      return customHeight;
    }

    // Same as the width above: expanded takes what the host gives us, not the
    // docked preset.
    if (surface === "expanded") {
      return height;
    }

    const presetHeight = widgetSizePreset ? widgetSizePreset.height + 78 : 610;

    const maxHeight = autoHeight ? modifiedFinalValue : presetHeight;
    return maxHeight ?? 610;
   }, [height,botConfig, widgetSizePreset, surface, isMobileViewport]);

  // The redesign's presetHeight above added 78 (its own header allowance) to
  // arrive at frameHeight, so subtracting 78 back off here returns exactly to
  // the preset's configured panel height — that pairing must stay 78/78.
  // Legacy never added anything on the way in, so subtracting 78 here instead
  // of the header's real 74px (see StyledWidgetHeader) quietly shorted the
  // legacy panel's own CSS height by 4px against what the iframe was actually
  // sized to, on top of the animation double-cap fixed above.
  const headerAllowance = isNewUi ? 78 : 74;
  const panelDeviceheight = frameHeight - headerAllowance;

  return (
    <StyledChatUIBoxWrap
      chatopened={chatOpened ? 1 : 0}
      hasSettled={hasSettled}
      devicewidth={frameWidth}
      deviceheight={panelDeviceheight}
      isFullScreen={isFullScreen}
      isMobileView={isMobileView}
      surface={surface}
      isMobileViewport={isMobileViewport}
    >
      <Header
        onFormSubmitted={onFormSubmitted}
        setShowStartScreen={setShowStartScreen}
        isPipInstance={isPipInstance}
      />
      <AppChatLoadingScreen loading={loading} />
      <AppChatScreenWrapper onFormSubmitted={onFormSubmitted} setDisableStartScreen={setDisableStartScreen} setShowStartScreen={setShowStartScreen} showStartScreen={showStartScreen}   />
      <AppInputHandler />
      {(botConfig?.config?.show_branding === undefined ||
        botConfig?.config?.show_branding === true) && <WidgetCopyright />}
    </StyledChatUIBoxWrap>
  );
};

export default MainUI;